// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers
import { classNames, useTranslate } from "helpers";

// 3. Validation & Types
import {
    FleetDocumentRow,
    FleetDocumentType,
    FleetDocumentUpload,
} from "services/api/fleet/vehicles/documents/types";

// 4. API & Data
import {
    createFleetDocumentAPI,
    deleteFleetDocumentUploadAPI,
    getFleetDocumentsAPI,
    getFleetDocumentTypesAPI,
} from "services/api/fleet/vehicles/documents/api";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// 5. UI Components
import FileUploadList from "ui/components/FileUpload/FileUploadList/FileUploadList";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";

// 6. Shared Components
import DocumentUploadModal from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleDocumentForm/components/DocumentUploadModal";

// 7. Local Types
import { TireSetDocumentFormProps } from "./types";


type SelectedFileType = {
    documentId: string;
    uploadId: string;
}

const initialSelectedFile = {
    documentId: "",
    uploadId: ""
}

/**
 * Dokumentacija seta guma: isti izgled kao korak dokumenata u čarobnjaku vozila. Svaki tip
 * dokumenta ima svoju karticu s gumbom "Dodaj", a unos ide kroz isti modal.
 */
const TireSetDocumentForm: React.FC<TireSetDocumentFormProps> = (props) => {
    const { formValues } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState<SelectedFileType>(initialSelectedFile);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [defaultDocumentTypeId, setDefaultDocumentTypeId] = useState<string | null>(null);
    const [documentTypes, setDocumentTypes] = useState<FleetDocumentType[]>([]);
    const [tireSetDocuments, setTireSetDocuments] = useState<
        DataWithPagination<FleetDocumentRow>
    >(initialDataWithPagination);

    useEffect(() => {
        fetchDocumentTypes();
        if (formValues.id)
            fetchTireSetDocuments(tireSetDocuments.pagination, formValues.id);
    }, [formValues.id]);

    const fetchTireSetDocuments = async (
        pagination: Pagination,
        tireSetId: string,
        name?: string
    ) => {
        setIsLoading(true);
        try {
            const results = await getFleetDocumentsAPI(
                pagination,
                name,
                tireSetId,
                "TireSet"
            );
            setTireSetDocuments(results);
        } catch {
            setTireSetDocuments(initialDataWithPagination);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDocumentTypes = async () => {
        setIsLoading(true);
        try {
            const results = await getFleetDocumentTypesAPI("", true);
            setDocumentTypes(results);
        } catch {
            setDocumentTypes([]);
        } finally {
            setIsLoading(false);
        }
    };

    /** Otvara upload modal — s predodabranim tipom ili bez */
    const handleOpenUploadModal = useCallback((documentTypeId?: string) => {
        setDefaultDocumentTypeId(documentTypeId ?? null);
        setShowUploadModal(true);
    }, []);

    const handleCloseUploadModal = useCallback(() => {
        setShowUploadModal(false);
    }, []);

    /** Sprema dokument iz upload modala */
    const handleUploadSave = useCallback(
        async (uploads: FleetDocumentUpload[], documentTypeId: string, name: string) => {
            if (!formValues.id || uploads.length === 0) return;

            setIsLoading(true);
            try {
                const formDocument = {
                    name,
                    description: null,
                    documentTypeId,
                    files: [],
                };
                await createFleetDocumentAPI(formValues.id, "TireSet", formDocument, uploads, t);
                await fetchTireSetDocuments(tireSetDocuments.pagination, formValues.id);
                setShowUploadModal(false);
            } catch {
                // error handled by API interceptors
            } finally {
                setIsLoading(false);
            }
        },
        [formValues.id, tireSetDocuments.pagination, t]
    );

    const handleUploadDeleteDialogClicked = useCallback(
        (documentId: string, uploadId: string) => {
            setSelectedFile({ documentId, uploadId });
            setOpenDialog(true);
        },
        []
    );

    const handleDocumentItemDeleteDialogConfirm = useCallback(async () => {
        if (!selectedFile) return;

        try {
            const deleted = await deleteFleetDocumentUploadAPI(
                selectedFile.documentId,
                selectedFile.uploadId,
                t
            );

            if (deleted && formValues.id) {
                await fetchTireSetDocuments(tireSetDocuments.pagination, formValues.id);
            }

            setOpenDialog(false);
            setSelectedFile(initialSelectedFile);
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [selectedFile, t, formValues.id, tireSetDocuments.pagination]);

    const handleDocumentItemDeleteDialogClose = useCallback(() => {
        setSelectedFile(initialSelectedFile);
        setOpenDialog(false);
    }, []);

    if (isLoading && !showUploadModal) {
        return (
            <div className="relative flex items-center justify-center px-4 h-[40vh]">
                <Loader isLoading={true}> </Loader>
            </div>
        );
    }

    return (
        <>
            <div className="w-full max-w-4xl mx-auto">
                {documentTypes.map((type) => {
                    const relatedDocs = tireSetDocuments.data.filter(
                        (doc) => doc.documentType.id === type.id
                    );
                    const uploads = relatedDocs.flatMap((doc) => doc.childRows || []);
                    const isEmpty = uploads.length === 0;

                    return (
                        <div key={type.id} className="px-4">
                            <div
                                className={classNames(
                                    "relative px-3 pt-2.5 pb-2 mb-2 border border-slate-200 rounded-sm ",
                                    isEmpty
                                        ? "bg-white hover:bg-blue-50 hover:border-blue-300 cursor-pointer"
                                        : ""
                                )}
                                onClick={isEmpty ? () => handleOpenUploadModal(type.id) : undefined}
                            >
                                <div className="flex justify-between">
                                    <div>
                                        <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                                            {type.name}
                                        </h3>
                                        {uploads.length > 0 && (
                                            <p className="text-xs leading-relaxed text-slate-500">
                                                {t("admin.fleet.vehicle.create.document.noresults")}
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon="/media/icons/plus.svg"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleOpenUploadModal(type.id);
                                            }}
                                        >
                                            Dodaj
                                        </Button>
                                    </div>
                                </div>
                                {uploads.length > 0 && (
                                    <div className="mt-2">
                                        <FileUploadList
                                            files={uploads.map((upload) => ({
                                                id: upload.id,
                                                originalFileName: upload.originalFileName,
                                                fileExtension: upload.originalFileName?.split(".").pop() ?? "",
                                                fileSizeKb: 0,
                                                contentType: "",
                                                created: new Date(),
                                                url: upload.url,
                                            }))}
                                            onDelete={(uploadId) => {
                                                const upload = uploads.find((u) => u.id === uploadId);
                                                if (upload) handleUploadDeleteDialogClicked(upload.documentId, upload.id);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <DocumentUploadModal
                show={showUploadModal}
                documentTypes={documentTypes}
                defaultDocumentTypeId={defaultDocumentTypeId}
                onSave={handleUploadSave}
                onClose={handleCloseUploadModal}
            />

            <Dialog
                open={openDialog}
                title={t("admin.fleet.vehicle.edit.document.dialog.delete.title")}
                message={t("admin.fleet.vehicle.edit.document.dialog.delete.message")}
                confirmText={t("admin.fleet.vehicle.edit.document.dialog.delete.confirmtext")}
                cancelText={t("admin.fleet.vehicle.edit.document.dialog.delete.canceltext")}
                onConfirm={handleDocumentItemDeleteDialogConfirm}
                onCancel={handleDocumentItemDeleteDialogClose}
            />
        </>
    );
};
export default memo(TireSetDocumentForm);
